import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.2 14.6 2.2 12 2.2 6.6 2.2 2.2 6.6 2.2 12s4.4 9.8 9.8 9.8c5.7 0 9.4-4 9.4-9.6 0-.6-.1-1.1-.2-1.6H12z" />
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    document.title = "Acceso a la redacción · El Noticiero";
    api.get("/auth/me").then(({ data }) => (data.is_admin ? navigate("/admin", { replace: true }) : setUser(data))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const logout = () => api.post("/auth/logout").finally(() => setUser(null));

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="admin-login-page">
      <div className="relative bg-ink text-white p-10 lg:p-16 flex flex-col justify-between overflow-hidden">
        <div className="absolute -right-24 -bottom-24 font-display font-black text-[28rem] leading-none text-white/[0.03] select-none">N</div>
        <div>
          <p className="font-display italic text-gold text-2xl -mb-2">El</p>
          <p className="font-display font-black text-6xl tracking-tight">Noticiero</p>
        </div>
        <div className="relative max-w-md mt-16">
          <p className="kicker text-gold">Redacción</p>
          <p className="font-display text-3xl sm:text-4xl leading-tight mt-3">Escribe, automatiza y publica tu periódico desde un único lugar.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 sm:p-16 bg-background">
        <div className="w-full max-w-sm">
          <p className="kicker text-primary">Acceso privado</p>
          <h1 className="font-display font-black text-4xl tracking-tight mt-2">Entrar a la redacción</h1>
          {user && !user.is_admin ? (
            <div className="mt-8 border border-border bg-card p-5" data-testid="admin-no-access">
              <ShieldAlert className="h-5 w-5 text-primary" />
              <p className="mt-3 font-semibold">La cuenta {user.email} no tiene acceso.</p>
              <p className="mt-1 text-sm text-muted-foreground">Pide al propietario del periódico que añada tu correo en Ajustes → Equipo con acceso.</p>
              <button onClick={logout} className="mt-4 kicker text-primary hover:underline" data-testid="admin-no-access-logout-btn">Usar otra cuenta</button>
            </div>
          ) : (
            <>
              <p className="mt-3 text-muted-foreground">Usa tu cuenta de Google. La primera persona que entra queda como propietaria del periódico.</p>
              {location.state?.error && <p className="mt-4 text-sm text-destructive" data-testid="admin-login-error">No se pudo completar el acceso. Inténtalo de nuevo.</p>}
              <button onClick={login} className="mt-8 w-full flex items-center justify-between gap-3 border-2 border-foreground px-5 py-4 font-semibold hover:bg-foreground hover:text-background transition-colors group" data-testid="admin-google-login-btn">
                <span className="flex items-center gap-3"><GoogleIcon /> Continuar con Google</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </>
          )}
          <a href="/" className="mt-10 inline-block kicker text-muted-foreground hover:text-primary" data-testid="admin-login-back-link">← Volver a la web</a>
        </div>
      </div>
    </div>
  );
}
